import { Injectable, inject, Injector } from '@angular/core';
// import { JwtHelperService } from '@auth0/angular-jwt';
import { Constants } from '../shared/constants';
import { Router } from '@angular/router';
import { EncryptionService } from './encryption.service';
import { StorageService } from './storage.service';
import { HttpClient } from '@angular/common/http';
import { Endpoint } from '../shared/endpoints';
import {
  IAuthResponse,
  IForgotPasswordPayload,
  IResetPasswordPayload,
  ISignInWithEmailPayload,
  ISignUpPayload,
  IUser,
} from '../../auth/models/auth.model';
import { UserProfileService } from './user-profile.service';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { IApiSuccessResponse } from '../interfaces/IApiResponse.model';
import { OnboardingStateService } from './onboarding-state.service';
import { DecryptionError } from '../errors/decryption.error';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // private jwtHelper = new JwtHelperService();
  private ENCRYPTION_KEY = `${environment.app}-${environment.appId}`;
  private returnUrl: string | null = null;
  private API_URL = `${environment.apiUrl}auth`;

  private http = inject(HttpClient);
  private storageService = inject(StorageService);
  private encryptionService = inject(EncryptionService);
  private injector = inject(Injector);
  private router = inject(Router);
  private onboardingStateService = inject(OnboardingStateService);

  private get userProfileService() {
    return this.injector.get(UserProfileService);
  }

  storeReturnUrl(url: string | null): void {
    this.returnUrl = url;
  }

  getReturnUrl(): string | null {
    return this.returnUrl;
  }

  getAccessToken(): string {
    try {
      return this.encryptionService.decrypt(
        this.ENCRYPTION_KEY,
        this.storageService.get(Constants.STORAGE_VARIABLES.TOKEN),
        Constants.STORAGE_VARIABLES.TOKEN,
      );
    } catch {
      console.warn('Session invalid or key change detected. Clearing session.');
      this.clearUserSessionData();
      return '';
    }
  }

  getUserData(): string {
    try {
      return this.encryptionService.decrypt(
        this.ENCRYPTION_KEY,
        this.storageService.get(Constants.STORAGE_VARIABLES.USER),
        Constants.STORAGE_VARIABLES.USER,
      );
    } catch {
      this.clearUserSessionData();
      this.clearUserProfile();
      return '';
    }
  }

  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    return token ? true : false;
    // return !this.jwtHelper.isTokenExpired(token);
  }

  isUserData() {
    return !!this.getUserData();
  }

  getUserProfileData() {
    return this.userProfileService.userProfileData;
  }

  clearUserProfile() {
    this.userProfileService.updateProfile(null);
  }

  clearUserSessionData() {
    this.onboardingStateService.clearOnboardingData();
    this.storageService.clear_all();
    this.userProfileService.updateProfile(null);
  }

  goToAuth(return_url?: string) {
    if (return_url) {
      this.router.navigate(['/auth/login'], {
        queryParams: { return_url: return_url },
      });
    } else {
      this.router.navigate(['/auth/sign-in']);
    }
  }

  logout() {
    // this.router.navigate(['/auth/logout'], { queryParams: { app: returnWebApp } });
    this.clearUserSessionData();
    this.goToAuth(this.router.routerState.snapshot.url);
  }

  // verifyToken(payload: ISsoToken): Observable<IAuthenticatedUserData> {
  //   return this.http.post<IAuthenticatedUserData>(`${Endpoint.AUTH.verify_token}`, {}, { params: { ...payload } }).pipe(
  //     map((response) => {
  //       return response;
  //     })
  //   );
  // }

  storeDetailsLocallyAndUpdateObservers(payload: IAuthResponse): void {
    try {
      this.storageService.set(
        Constants.STORAGE_VARIABLES.TOKEN,
        this.encryptionService.encrypt(
          this.ENCRYPTION_KEY,
          payload.accessToken,
          Constants.STORAGE_VARIABLES.TOKEN,
        ),
      );
      this.storageService.set(
        Constants.STORAGE_VARIABLES.USER,
        this.encryptionService.encrypt(
          this.ENCRYPTION_KEY,
          JSON.stringify(payload.user),
          Constants.STORAGE_VARIABLES.USER,
        ),
      );
      if (payload.user.role === Constants.STORAGE_VARIABLES.TENANT) {
        this.storageService.set(
          Constants.STORAGE_VARIABLES.TENANT,
          this.encryptionService.encrypt(
            this.ENCRYPTION_KEY,
            JSON.stringify(payload.user),
            Constants.STORAGE_VARIABLES.TENANT,
          ),
        );
      }
    } catch {
      /* empty */
    } finally {
      this.updateUserProfileService();
    }
  }

  storeUserDetailsLocallyAndUpdateObservers(payload: IUser) {
    try {
      this.storageService.set(
        Constants.STORAGE_VARIABLES.USER,
        this.encryptionService.encrypt(
          this.ENCRYPTION_KEY,
          JSON.stringify(payload),
          Constants.STORAGE_VARIABLES.USER,
        ),
      );
    } catch {
      /* empty */
    } finally {
      this.updateUserProfileService();
    }
  }

  updateUserProfileService(): void {
    try {
      const userObj = this.encryptionService.decrypt(
        this.ENCRYPTION_KEY,
        this.storageService.get(Constants.STORAGE_VARIABLES.USER),
        Constants.STORAGE_VARIABLES.USER,
      );

      if (userObj) {
        try {
          this.userProfileService.updateProfile(JSON.parse(userObj));
        } catch (error) {
          console.error('Failed to parse user profile data, clearing session', error);
          this.clearUserSessionData();
        }
      } else {
        // Empty data - ensure profile is cleared
        this.userProfileService.updateProfile(null);
      }
    } catch (error) {
      if (error instanceof DecryptionError) {
        console.error('Decryption failed for user data, clearing corrupted storage', error);
        // Clear the corrupted data from storage
        if (error.storageKey) {
          this.storageService.clear(error.storageKey);
        }
      } else {
        console.error('An unexpected error occurred while updating the user profile', error);
      }
      // Clear profile to prevent stale data
      this.userProfileService.updateProfile(null);
    }
  }

  signInWithEmail(payload: ISignInWithEmailPayload): Observable<IAuthResponse> {
    return this.http.post<IAuthResponse>(Endpoint.AUTH.login, payload);
  }

  signInWithGoogle(payload: { refreshToken: string }): Observable<IAuthResponse> {
    return this.http.post<IAuthResponse>(Endpoint.AUTH.refresh_token, payload);
  }

  signUp(payload: ISignUpPayload): Observable<IAuthResponse> {
    return this.http.post<IAuthResponse>(Endpoint.AUTH.register, payload);
  }

  verifyEmail(token: string): Observable<IAuthResponse> {
    const url = `${Endpoint.AUTH.verify_email}?token=${token}`;
    return this.http.get<IAuthResponse>(url);
  }

  resendVerificationEmail(): Observable<IApiSuccessResponse<{ message: string }>> {
    return this.http.post<IApiSuccessResponse<{ message: string }>>(
      Endpoint.AUTH.resend_verification,
      {},
    );
  }

  forgotPassword(
    payload: IForgotPasswordPayload,
  ): Observable<IApiSuccessResponse<{ message: string }>> {
    return this.http.post<IApiSuccessResponse<{ message: string }>>(
      Endpoint.AUTH.forgot_password,
      payload,
    );
  }

  resetPassword(
    payload: IResetPasswordPayload,
  ): Observable<IApiSuccessResponse<{ message: string }>> {
    return this.http.post<IApiSuccessResponse<{ message: string }>>(
      Endpoint.AUTH.reset_password,
      payload,
    );
  }
}
